import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import Seo from "@/components/Seo";
import RecommendedForYou from "@/components/RecommendedForYou";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, Sparkles, TrendingUp, LineChart, ShieldCheck, Zap, Store } from "lucide-react";
import BrandsFeed from "@/components/BrandsFeed";
import PwaInstallNudge from "@/components/PwaInstallNudge";

export default function Home() {
  const [coupons, setCoupons] = useState([]);
  const [flash, setFlash] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/coupons?featured=true&limit=6").then((r) => setCoupons(r.data));
    api.get("/coupons?flash=true&limit=4").then((r) => setFlash(r.data));
    api.get("/stores?featured=true&limit=12").then((r) => setStores(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const totalOffers = coupons.length + flash.length;

  return (
    <div className="relative">
      <Seo
        title="Discover verified deals in Canada & the US"
        description="Cyannect is the marketplace where shoppers save real money and verified brands track every click, conversion and dollar. Free forever plan, made in Canada."
        keywords="coupons canada, deals us, discount codes, verified brands, cashback"
        path="/"
      />
      {/* Floating blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="blob blob-cyan blob-float w-[600px] h-[600px] -top-40 -left-40" />
        <div className="blob blob-lav blob-float w-[500px] h-[500px] top-1/3 -right-40" style={{ animationDelay: "3s" }} />
        <div className="blob blob-mint blob-float w-[500px] h-[500px] top-2/3 left-1/4" style={{ animationDelay: "6s" }} />
        <div className="blob blob-rose blob-float w-[400px] h-[400px] top-1/2 right-1/4 opacity-40" style={{ animationDelay: "9s" }} />
      </div>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 pt-20 pb-24 relative z-10">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 pf-chip pf-chip-cyan">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
              Live · Canada + United States
            </div>
            <h1 className="font-display font-medium mt-8 leading-[1.05] tracking-[-0.035em]">
              <div className="text-4xl md:text-5xl lg:text-6xl text-slate-900">A softer way to</div>
              <div className="text-4xl md:text-5xl lg:text-6xl pf-gradient-text gradient-shift mt-1 pb-2">save and grow.</div>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
              Cyannect is the performance commerce layer where shoppers discover real deals and merchants track every click, conversion and dollar — beautifully.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/coupons" className="pf-btn-primary px-6 py-3 rounded-full flex items-center gap-2" data-testid="hero-browse-coupons">
                Browse coupons
              </Link>
              <Link to="/register?role=merchant" className="pf-btn-glass px-6 py-3 rounded-full font-medium flex items-center gap-2" data-testid="hero-for-merchants">
                For merchants <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Live deal highlight card */}
          <div className="mt-16 grid md:grid-cols-2 gap-6 fade-up">
            <Link to="/coupons?featured=true" className="glass-strong rounded-3xl p-6 flex items-center gap-6 shimmer hover:-translate-y-0.5 transition-transform" data-testid="hero-live-deal">
              <div className="w-16 h-16 rounded-2xl pf-gradient-bg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Live now</div>
                <div className="font-display text-2xl mt-1 text-slate-900">$150 off MacBook Air M3</div>
                <div className="text-sm text-slate-500 mt-1">Best Buy · 2,341 shoppers used this today</div>
              </div>
            </Link>
            <Link to="/coupons?flash=true" className="glass-strong rounded-3xl p-6 flex items-center gap-6 shimmer hover:-translate-y-0.5 transition-transform" data-testid="hero-flash-deal">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Flash · ends today</div>
                <div className="font-display text-2xl mt-1 text-slate-900">BOGO 50% off — Sport Chek</div>
                <div className="text-sm text-slate-500 mt-1">Mix &amp; match shoes and apparel</div>
              </div>
            </Link>
          </div>
        </div>

        {/* Brand marquee */}
        <div className="glass border-y border-white/60 overflow-hidden relative z-10">
          <div className="flex whitespace-nowrap marquee py-5">
            {[...stores, ...stores, ...stores].map((s, i) => (
              <div key={i} className="mx-10 flex items-center gap-3 text-sm font-medium text-slate-500">
                <img src={s.logo_url} alt="" className="w-7 h-7 rounded-full ring-2 ring-white shadow-sm" />
                <span className="uppercase tracking-widest text-xs">{s.name}</span>
              </div>
            ))}
          </div>
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-cyan-50/80 to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-cyan-50/80 to-transparent pointer-events-none" />
        </div>
      </section>

      {/* Brand-First carousel — storefront-forward discovery (§28-34). */}
      <section className="max-w-[1400px] mx-auto px-6 py-16 relative z-10" data-testid="home-brand-carousel">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="pf-chip pf-chip-cyan"><Store className="w-3 h-3" /> Shop by brand</div>
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 mt-3">Discover your favourite brands</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-lg">One tap to explore every offer inside a merchant — from featured drops to hidden storewide savings.</p>
          </div>
          <Link to="/brands" className="text-sm text-cyan-700 font-semibold hover:underline hidden md:inline"
                data-testid="home-brands-see-all">See all brands →</Link>
        </div>
        <BrandsFeed limit={8} compact />
      </section>

      <RecommendedForYou user={user} />

      {/* PERFORMANCE STATS */}
      <section className="relative py-24 z-10">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-8">
              <div className="pf-chip pf-chip-lav">Performance</div>
              <h2 className="font-display text-4xl md:text-6xl font-medium tracking-[-0.03em] mt-4 leading-[1]">
                Every transaction, <span className="pf-gradient-text">measured.</span>
              </h2>
            </div>
            <div className="md:col-span-4">
              <p className="text-slate-600 leading-relaxed">
                The performance commerce network handling millions of interactions across coupons, clicks and conversions — with real-time attribution built in.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total transactions", value: "2.4M+", chip: "pf-chip-cyan" },
              { label: "Live offers", value: `${totalOffers}+`, chip: "pf-chip-lav" },
              { label: "Verified merchants", value: `${stores.length}+`, chip: "pf-chip-mint" },
              { label: "Uptime", value: "99.99%", chip: "pf-chip-cyan" },
            ].map((s, i) => (
              <div key={i} className="pf-card p-7 flex flex-col justify-between min-h-[180px] relative overflow-hidden">
                <span className={`pf-chip ${s.chip} w-fit`}>{s.label}</span>
                <div className="font-display text-5xl md:text-6xl font-medium tracking-tight mt-6 tabular-nums text-slate-900">
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="pf-divider" />

      {/* Flash sales */}
      {flash.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="pf-chip pf-chip-lav"><Zap className="w-3 h-3 mr-1" /> Flash sales</div>
              <h2 className="font-display text-4xl md:text-6xl font-medium tracking-[-0.03em] mt-4">
                Ending <span className="pf-gradient-text">soon.</span>
              </h2>
            </div>
            <Link to="/coupons?flash=true" className="pf-btn-glass px-5 py-2.5 rounded-full text-sm font-medium">View all →</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {flash.map((c) => <CouponCard key={c.id} coupon={c} />)}
          </div>
        </section>
      )}

      <div className="pf-divider" />

      {/* Featured coupons */}
      <section className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="pf-chip">Editor's picks</div>
            <h2 className="font-display text-4xl md:text-6xl font-medium tracking-[-0.03em] mt-4">
              What's <span className="pf-gradient-text">happening</span> now
            </h2>
          </div>
          <Link to="/coupons" className="pf-btn-glass px-5 py-2.5 rounded-full text-sm font-medium">All coupons →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
        </div>
      </section>

      <div className="pf-divider" />

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        <div className="mb-10">
          <div className="pf-chip pf-chip-mint">Discover</div>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-[-0.03em] mt-4">
            Explore <span className="pf-gradient-text">every category.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => {
            const styles = [
              "bg-gradient-to-br from-cyan-100 to-sky-100 border-cyan-200",
              "bg-gradient-to-br from-emerald-100 to-emerald-100 border-emerald-200",
              "bg-gradient-to-br from-emerald-100 to-teal-100 border-emerald-200",
              "bg-gradient-to-br from-rose-100 to-orange-100 border-rose-200",
            ];
            return (
              <Link
                key={c.id}
                to={`/coupons?category=${c.slug}`}
                className={`pf-card p-6 flex flex-col justify-between h-40 relative overflow-hidden group !border ${styles[i % styles.length]}`}
                data-testid={`category-${c.slug}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-semibold">{c.coupon_count} coupons</div>
                <div className="font-display text-2xl font-medium text-slate-900">{c.name}</div>
                <ArrowUpRight className="w-4 h-4 absolute top-4 right-4 text-slate-500 group-hover:text-slate-900 transition-colors" />
              </Link>
            );
          })}
        </div>
      </section>

      <div className="pf-divider" />

      {/* Top stores */}
      <section className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="pf-chip">Trusted partners</div>
            <h2 className="font-display text-4xl md:text-6xl font-medium tracking-[-0.03em] mt-4">
              The world's <span className="pf-gradient-text">top retailers.</span>
            </h2>
          </div>
          <Link to="/stores" className="pf-btn-glass px-5 py-2.5 rounded-full text-sm font-medium">All stores →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stores.slice(0, 8).map((s) => (
            <Link to={`/store/${s.slug}`} key={s.id} className="pf-card p-5 flex items-center gap-4" data-testid={`store-tile-${s.slug}`}>
              <img src={s.logo_url} alt={s.name} className="w-12 h-12 rounded-xl border border-white/80 bg-white object-contain" data-logo="1" />
              <div>
                <div className="font-display text-lg font-medium text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500">{s.coupon_count} offers</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="pf-divider" />

      {/* Merchant pitch */}
      <section id="pricing" className="relative overflow-hidden py-24 z-10">
        <div className="max-w-[1400px] mx-auto px-6 relative">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <div className="pf-chip pf-chip-mint">For merchants</div>
              <h2 className="font-display text-4xl md:text-6xl font-medium tracking-[-0.03em] mt-4 leading-[1]">
                Build alongside <br />
                <span className="pf-gradient-text">our network.</span>
              </h2>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
                Cyannect isn't just a coupon site. It's a performance layer for your business — with analytics, campaign controls and payment orchestration built in.
              </p>
              <div className="mt-10 grid sm:grid-cols-2 gap-3 max-w-xl">
                {[
                  { icon: LineChart, t: "Real-time analytics" },
                  { icon: TrendingUp, t: "CPC & subscription plans" },
                  { icon: ShieldCheck, t: "Fraud protection" },
                  { icon: Sparkles, t: "Featured placement" },
                ].map((f, i) => (
                  <div key={i} className="pf-card p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center">
                      <f.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm text-slate-900 font-medium">{f.t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <Link to="/register?role=merchant" className="pf-btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="merchant-cta">
                  Become a partner <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="pf-card p-8 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-72 h-72 pf-gradient-bg rounded-full blur-3xl opacity-40" />
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold relative">Plans starting</div>
                <div className="font-display text-7xl font-medium mt-4 tracking-tight text-slate-900 relative">
                  $0<span className="text-2xl text-slate-500 font-normal">/mo</span>
                </div>
                <p className="text-slate-600 text-sm mt-3 leading-relaxed relative">
                  Pay only for verified clicks. Upgrade to unlimited coupons and advanced analytics anytime.
                </p>
                <ul className="mt-8 space-y-3 text-sm text-slate-700 relative">
                  {[
                    "Coupon marketplace listing",
                    "Click & conversion tracking",
                    "Geo-targeting (province/city)",
                    "Payment orchestration (soon)",
                    "White-label API (Enterprise)",
                  ].map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
